import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '@/App';
import { ArrowLeft, Users, Package, Plane, Building2, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const [statsRes, partnersRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/partners`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

      if (partnersRes.ok) {
        const partnersData = await partnersRes.json();
        setPartners(partnersData.partners || []);
      }
    } catch (error) {
      toast.error('Error fetching data');
    } finally {
      setLoading(false);
    }
  };

  const approvePartner = async (partnerId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/partners/${partnerId}/approve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Partner approved');
        fetchData();
      } else {
        toast.error('Failed to approve partner');
      }
    } catch (error) {
      toast.error('Error approving partner');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="text-white text-xl" style={{ fontFamily: 'Manrope' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] p-8" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-white/60 hover:text-white mb-4 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span style={{ fontFamily: 'Manrope' }}>Back to Dashboard</span>
          </button>
          <h1 className="text-5xl font-bold text-white mb-2" style={{ fontFamily: 'Sora' }}>Admin Dashboard</h1>
          <p className="text-white/60" style={{ fontFamily: 'Manrope' }}>Manage ZN Networks platform</p>
        </div>

        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
            <div className="card-glass p-6 border-l-4 border-[#FFD60A]" data-testid="stat-users">
              <div className="flex items-center justify-between mb-2">
                <Users className="w-5 h-5 text-[#FFD60A]" />
                <span className="text-xs text-white/40 uppercase tracking-wider" style={{ fontFamily: 'Manrope' }}>Total Users</span>
              </div>
              <div className="text-3xl font-bold text-white" style={{ fontFamily: 'Sora' }}>{stats.total_users}</div>
            </div>

            <div className="card-glass p-6 border-l-4 border-[#FF3B30]" data-testid="stat-shipments">
              <div className="flex items-center justify-between mb-2">
                <Package className="w-5 h-5 text-[#FF3B30]" />
                <span className="text-xs text-white/40 uppercase tracking-wider" style={{ fontFamily: 'Manrope' }}>Shipments</span>
              </div>
              <div className="text-3xl font-bold text-white" style={{ fontFamily: 'Sora' }}>{stats.total_shipments}</div>
            </div>

            <div className="card-glass p-6 border-l-4 border-[#3B82F6]" data-testid="stat-bookings">
              <div className="flex items-center justify-between mb-2">
                <Plane className="w-5 h-5 text-[#3B82F6]" />
                <span className="text-xs text-white/40 uppercase tracking-wider" style={{ fontFamily: 'Manrope' }}>Bookings</span>
              </div>
              <div className="text-3xl font-bold text-white" style={{ fontFamily: 'Sora' }}>{stats.total_bookings}</div>
            </div>

            <div className="card-glass p-6 border-l-4 border-[#10B981]" data-testid="stat-partners">
              <div className="flex items-center justify-between mb-2">
                <Building2 className="w-5 h-5 text-[#10B981]" />
                <span className="text-xs text-white/40 uppercase tracking-wider" style={{ fontFamily: 'Manrope' }}>Partners</span>
              </div>
              <div className="text-3xl font-bold text-white" style={{ fontFamily: 'Sora' }}>{stats.total_partners}</div>
            </div>

            <div className="card-glass p-6 border-l-4 border-[#F59E0B]" data-testid="stat-pending">
              <div className="flex items-center justify-between mb-2">
                <TrendingUp className="w-5 h-5 text-[#F59E0B]" />
                <span className="text-xs text-white/40 uppercase tracking-wider" style={{ fontFamily: 'Manrope' }}>Pending</span>
              </div>
              <div className="text-3xl font-bold text-white" style={{ fontFamily: 'Sora' }}>{stats.pending_partners}</div>
            </div>
          </div>
        )}

        {/* Pending Partners */}
        <div>
          <h2 className="text-3xl font-bold text-white mb-6" style={{ fontFamily: 'Sora' }}>Pending Partner Applications</h2>
          {partners.filter(p => p.verification_status === 'pending').length > 0 ? (
            <div className="space-y-4">
              {partners.filter(p => p.verification_status === 'pending').map((partner) => (
                <div key={partner.id} className="card-glass p-6" data-testid={`partner-${partner.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Sora' }}>{partner.business_name}</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-white/60">Contact: </span>
                          <span className="text-white">{partner.full_name}</span>
                        </div>
                        <div>
                          <span className="text-white/60">Email: </span>
                          <span className="text-white">{partner.email}</span>
                        </div>
                        <div>
                          <span className="text-white/60">Phone: </span>
                          <span className="text-white">{partner.phone}</span>
                        </div>
                      </div>
                      <div className="mt-2">
                        <span className="px-3 py-1 bg-white/5 text-white/60 text-xs uppercase tracking-wider">
                          {partner.partner_type}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => approvePartner(partner.id)}
                      className="btn-primary ml-4"
                      data-testid={`approve-${partner.id}`}
                    >
                      Approve
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="card-glass p-12 text-center">
              <Building2 className="w-16 h-16 text-white/20 mx-auto mb-4" />
              <p className="text-white/60" style={{ fontFamily: 'Manrope' }}>No pending applications</p>
            </div>
          )}
        </div>

        {/* Approved Partners */}
        <div className="mt-8">
          <h2 className="text-3xl font-bold text-white mb-6" style={{ fontFamily: 'Sora' }}>Approved Partners</h2>
          {partners.filter(p => p.verification_status === 'approved').length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {partners.filter(p => p.verification_status === 'approved').map((partner) => (
                <div key={partner.id} className="card-glass p-6" data-testid={`approved-partner-${partner.id}`}>
                  <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Sora' }}>{partner.business_name}</h3>
                  <p className="text-white/60 text-sm mb-1">{partner.full_name}</p>
                  <p className="text-white/60 text-sm mb-3">{partner.email}</p>
                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1 bg-[#10B981]/20 text-[#10B981] text-xs uppercase tracking-wider">
                      {partner.verification_status}
                    </span>
                    <span className="px-3 py-1 bg-white/5 text-white/60 text-xs uppercase tracking-wider">
                      {partner.tier_level}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="card-glass p-12 text-center">
              <Building2 className="w-16 h-16 text-white/20 mx-auto mb-4" />
              <p className="text-white/60" style={{ fontFamily: 'Manrope' }}>No approved partners</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;